import type { ConversationAttributesData } from '@/models/consultation/conversations.js'
import {
  rand,
  randBoolean,
  randNumber,
  randPastDate,
  randRecentDate,
  randText,
} from '@ngneat/falso'
import { Factory, Model, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MockServer } from '../server.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import { createErrorResponse, pageable } from '../utils.js'

export const model = (
  Model as ModelDefinition<ConversationAttributesData>
).extend({
  consultation: belongsTo('consultation'),
  creator: belongsTo('user'),
  modifier: belongsTo('user'),
})

export const factory = Factory.extend<ConversationAttributesData>({
  id: () => randNumber({ max: 100000 }),
  creator_id: () => randNumber({ max: 100 }),
  created_at: () => randPastDate().toISOString(),
  modifier_id: () => (randBoolean() ? randNumber({ max: 100 }) : null),
  updated_at: () => (randBoolean() ? randRecentDate().toISOString() : null),
  transcription: () => [
    'Patient reported feeling unwell',
    'Examined patient thoroughly',
    'Prescribed medication',
  ],
  summary: () => (randBoolean() ? randText() : undefined),
  /**
   * ID of a clinical note template, or use -1 for the SOAP and -2 for the narrative template
   * @default -1
   */
  template_id: () => rand([-1, -2]),
  /**
   * Recording duration in seconds
   */
  recording_duration: () => randNumber({ min: 30, max: 1800 }),

  withSummary: trait({
    summary: () => randText(),
    updated_at: () => randRecentDate().toISOString(),
  }),

  withoutSummary: trait({
    summary: undefined,
    updated_at: null,
  }),

  withTemplate: trait({
    template_id: () => randNumber({ max: 100 }),
  }),
})

export function routes(server: MockServer) {
  server.post('/api/ai/consultation_conversations', (schema, request) => {
    const { data } = JSON.parse(request.requestBody)
    const attributes = data.attributes

    if (!attributes.consultation_id || !attributes.transcription) {
      return createErrorResponse({
        status: 400,
        errors: [
          {
            detail: 'Consultation ID and transcription are required',
          },
        ],
      })
    }

    const conversationData = {
      ...attributes,
      creator_id: randNumber({ max: 100 }),
      created_at: getTodayDateTimeISO(),
      modifier_id: null,
      updated_at: null,
      summary: null,
      recording_duration:
        attributes.recording_duration ?? randNumber({ min: 30, max: 1800 }),
    }

    const conversation = schema.create(
      'consultationConversation',
      conversationData,
    )

    // Set up the consultation relationship
    const consultation = schema.find(
      'consultation',
      attributes.consultation_id.toString(),
    )
    if (consultation) {
      conversation.update({ consultation })
    }

    return new Response(
      JSON.stringify({
        data: {
          type: 'ConsultationConversationFullInfo',
          attributes: conversation.attrs,
        },
      }),
      { status: 202 },
    )
  })

  server.get(
    '/api/ai/consultation_conversations/:id/conversations',
    (schema, request) => {
      const consultationId = request.params.id
      const conversations = schema.where(
        'consultationConversation',
        (conversation) => {
          return conversation.consultation?.id?.toString() === consultationId
        },
      )

      const { data, meta } = pageable(conversations, request)

      return {
        data: data.models.map((conversation) => ({
          type: 'ConsultationConversationFullInfo',
          attributes: conversation.attrs,
        })),
        meta,
      }
    },
  )

  server.get(
    '/api/ai/consultation_conversations/:id/conversations/:conversationId',
    (schema, request) => {
      const consultationId = request.params.id
      const conversationId = request.params.conversationId

      const conversation = schema.find(
        'consultationConversation',
        conversationId,
      )

      if (
        !conversation ||
        conversation.consultation?.id?.toString() !== consultationId
      ) {
        return createErrorResponse({
          status: 404,
          errors: [
            {
              detail: 'Conversation not found',
            },
          ],
        })
      }

      return {
        data: {
          type: 'ConsultationConversation',
          id: conversation.id,
          attributes: conversation.attrs,
        },
      }
    },
  )

  server.patch(
    '/api/ai/consultation_conversations/:id/conversations/:conversationId',
    (schema, request) => {
      const consultationId = request.params.id
      const conversationId = request.params.conversationId
      const { data } = JSON.parse(request.requestBody)
      const attributes = data.attributes

      const conversation = schema.find(
        'consultationConversation',
        conversationId,
      )

      if (
        !conversation ||
        conversation.consultation?.id?.toString() !== consultationId
      ) {
        return createErrorResponse({
          status: 404,
          errors: [
            {
              detail: 'Conversation not found',
            },
          ],
        })
      }

      const hasTranscriptionUpdate =
        attributes.transcription &&
        JSON.stringify(attributes.transcription) !==
          JSON.stringify(conversation.attrs.transcription)

      conversation.update({
        ...attributes,
        modifier_id: randNumber({ max: 100 }),
        updated_at: getTodayDateTimeISO(),
        // Reset summary if transcription changed
        summary: hasTranscriptionUpdate ? null : conversation.attrs.summary,
      })

      // Return 202 if transcription changed (new summary will be generated)
      // Return 200 if only other fields changed
      const status = hasTranscriptionUpdate ? 202 : 200

      return new Response(
        JSON.stringify({
          data: {
            type: 'ConsultationConversation',
            id: conversation.id,
            attributes: conversation.attrs,
          },
        }),
        { status },
      )
    },
  )

  server.delete(
    '/api/ai/consultation_conversations/:id/conversations/:conversationId',
    (schema, request) => {
      const consultationId = request.params.id
      const conversationId = request.params.conversationId

      const conversation = schema.find(
        'consultationConversation',
        conversationId,
      )

      if (
        !conversation ||
        conversation.consultation?.id?.toString() !== consultationId
      ) {
        return createErrorResponse({
          status: 404,
          errors: [
            {
              detail: 'Conversation not found',
            },
          ],
        })
      }

      conversation.destroy()

      return {
        meta: {
          message: `Deleted conversation ${conversationId} for consultation ${consultationId}`,
        },
      }
    },
  )

  server.delete('/api/ai/consultation_conversations/:id', (schema, request) => {
    const consultationId = request.params.id
    const conversations = schema.where(
      'consultationConversation',
      (conversation) => {
        return conversation.consultation?.id?.toString() === consultationId
      },
    )

    conversations.models.forEach((conversation) => {
      conversation.destroy()
    })

    return {
      meta: {
        message: `Deleted ${conversations.models.length} conversation(s) for consultation ${consultationId}`,
      },
    }
  })
}

export function seeds(server: MockServer) {
  for (let i = 1; i <= 10; i++) {
    const conversation = server.create('consultationConversation', {
      transcription: [
        'Veterinarian: How can I help you today?',
        'Client: My dog has been acting lethargic',
        'Veterinarian: How long has this been going on?',
        'Client: About 3 days now',
      ],
      summary: randBoolean() ? randText() : null,
    })

    // Set up the consultation relationship
    const consultation = server.schema.find('consultation', i.toString())

    if (consultation) {
      conversation.update({ consultation })
    }
  }
}
